import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { ORIGIN } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import {
  UNDEFINED_CHART_FEATURES,
  UNDEFINED_LOCKS,
  UNDEFINED_MPC_LOCKS
} from "entities/charts/chart";
import {
  DEFAULT_AVERAGE_FEATURE_ENTITY,
  DEFAULT_BEST_FIT_FEATURE_ENTITY,
  DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY,
  DEFAULT_CHART_FOCUS_ENTITY,
  DEFAULT_CHART_TYPE_PARAM_FEATURE,
  DEFAULT_CUT_OFF_FEATURE_ENTITY,
  DEFAULT_DATA_RANGE_SELECT_FEATURE_ENTITY,
  DEFAULT_DATA_TABLE_FEATURE_ENTITY,
  DEFAULT_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY,
  DEFAULT_JITTER_PLOT_FEATURE_ENTITY,
  DEFAULT_LASSO_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LOG_AXIS_FEATURE_ENTITY,
  DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY,
  DEFAULT_MULTI_PRODUCT_ENTITY,
  DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY,
  DEFAULT_NORMALIZATION_ENTITY,
  DEFAULT_P10_FEATURE_ENTITY,
  DEFAULT_P50_FEATURE_ENTITY,
  DEFAULT_P90_FEATURE_ENTITY,
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  DEFAULT_POINT_SIZE_FEATURE_ENTITY,
  DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY,
  DEFAULT_SCREENSHOT_FEATURE_ENTITY,
  DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY,
  DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
  DEFAULT_SOURCE_FEATURE_ENTITY,
  DEFAULT_TIME_STEP_FEATURE_ENTITY,
  DEFAULT_TOTAL_FLUID_CHART_SETTINGS_ENTITY,
  DEFAULT_VIOLIN_PLOT_FEATURE_ENTITY,
  DEFAULT_X_AXIS_SORT_ENTITY
} from "entities/charts/eva/features";
import { selectChart } from "store/features";
import { RootState } from "store/rootReducer";
import { BoxPlotBinsFeature } from "types/charts/feature";
import { LockState, MpcAxisLockMap } from "types/charts/locks";
import { ChartRequestType } from "types/charts/requests";

import { useChartFocusUnitCorrection } from "./useChartFocusUnitCorrection";

const DEFAULT_BOX_PLOT_BINS_ENTITY: Partial<BoxPlotBinsFeature> = {
  show: false,
  enabled: false,
  active: false
};

/**
 * useChartEntities
 *
 * This hook provides access to all chart entity state for a given chart ID.
 *
 * PERFORMANCE: This hook uses a single Redux subscription with shallowEqual comparison
 * to minimize re-renders. All values are derived from the single chart entity in a
 * memoized computation, reducing subscription overhead from 60+ to 1.
 *
 * @param id - The unique identifier for the chart
 * @returns All chart entity state values
 */
export const useChartEntities = (id: string) => {
  // Single subscription to the entire chart entity - reduces 60+ subscriptions to 1
  const chart = useSelector((state: RootState) => selectChart(state, id), shallowEqual);

  // Extract chartFocusRaw for unit correction hook (must be called before useMemo)
  const chartFocusRaw = chart?.features?.chartFocus ?? DEFAULT_CHART_FOCUS_ENTITY;
  const chartFocus = useChartFocusUnitCorrection(chartFocusRaw);

  // Derive all values from the single chart entity in one memoized computation
  return useMemo(() => {
    if (!chart) {
      return {
        ...DEFAULT_CHART_ENTITIES,
        chartFocus
      };
    }

    const features = chart.features ?? UNDEFINED_CHART_FEATURES;

    return {
      chartType: chart.chartType ?? EvaChart.None,
      overrideSizeLimit: chart.overrideSizeLimit ?? false,
      product: chart.product,
      sum: features.spaghetti ?? DEFAULT_FEATURE_ENTITY,
      forecast: features.forecast ?? DEFAULT_FEATURE_ENTITY,
      source: features.source ?? DEFAULT_SOURCE_FEATURE_ENTITY,
      timeStep: features.timeStep ?? DEFAULT_TIME_STEP_FEATURE_ENTITY,
      lasso: features.lasso ?? DEFAULT_LASSO_FEATURE_ENTITY,
      isProducingRateType: features.isProducingRateType ?? DEFAULT_SOURCE_FEATURE_ENTITY,
      cutoff: (features.cutoff ?? DEFAULT_CUT_OFF_FEATURE_ENTITY)?.properties?.value,
      shutInMonths: (features.shutInMonths ?? DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY)
        ?.properties?.value,
      movingAverageDays: (
        features.movingAverageDays ?? DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY
      )?.properties?.value,
      survivorBias: (features.survivorBias ?? DEFAULT_FEATURE_ENTITY)?.active,
      survivorBiasAsForecast: features.survivorBiasAsForecast ?? DEFAULT_FEATURE_ENTITY,
      useWeightedRatioAverage: (
        features.useWeightedRatioAverage ?? DEFAULT_FEATURE_ENTITY
      )?.active,
      lockUnits: (features.lockUnits ?? DEFAULT_FEATURE_ENTITY)?.active,
      debugState: features.debug ?? DEFAULT_FEATURE_ENTITY,
      typewells: features.typewells ?? DEFAULT_FEATURE_ENTITY,
      workingForecast: features.workingForecast ?? DEFAULT_FEATURE_ENTITY,
      fullScreen: features.fullScreen ?? DEFAULT_FEATURE_ENTITY,
      screenshot: features.screenshot ?? DEFAULT_SCREENSHOT_FEATURE_ENTITY,
      average: features.average ?? DEFAULT_AVERAGE_FEATURE_ENTITY,
      p10: features.p10 ?? DEFAULT_P10_FEATURE_ENTITY,
      p50: features.p50 ?? DEFAULT_P50_FEATURE_ENTITY,
      p90: features.p90 ?? DEFAULT_P90_FEATURE_ENTITY,
      normalizeBy: features.normalization ?? DEFAULT_NORMALIZATION_ENTITY,
      chartFocus,
      multiProductFocus: features.multiProductFocus ?? DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY,
      showWellCount: (features.showWellCount ?? DEFAULT_FEATURE_ENTITY)?.active,
      chartLegend: features.legend ?? DEFAULT_FEATURE_ENTITY,
      bounds: chart.bounds,
      dataTable: features.datatable ?? DEFAULT_DATA_TABLE_FEATURE_ENTITY,
      overflowIndex: chart.overflowIndex,
      pointOpacity: features.pointOpacity ?? DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
      pointSize: features.pointSize ?? DEFAULT_POINT_SIZE_FEATURE_ENTITY,
      lineSize: features.lineSize ?? DEFAULT_LINE_SIZE_FEATURE_ENTITY,
      lineOpacity: features.lineOpacity ?? DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
      highlightedLineSize:
        features.highlightedLineSize ?? DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
      highlightedLineOpacity:
        features.highlightedLineOpacity ??
        DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
      useSeriesStyleOverride: features.useSeriesStyleOverride ?? DEFAULT_FEATURE_ENTITY,
      chartTypeParam: features.chartTypeParam ?? DEFAULT_CHART_TYPE_PARAM_FEATURE,
      annotations: features.annotations ?? DEFAULT_FEATURE_ENTITY,
      histogramToggle:
        features.histogramToggle ?? DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY,
      jitterPlotToggle: features.jitterPlotToggle ?? DEFAULT_JITTER_PLOT_FEATURE_ENTITY,
      violinPlotToggle: features.violinPlotToggle ?? DEFAULT_VIOLIN_PLOT_FEATURE_ENTITY,
      boxPlotBins: features.boxPlotBins ?? DEFAULT_BOX_PLOT_BINS_ENTITY,
      dataRangeSelect:
        features.dataRangeSelect ?? DEFAULT_DATA_RANGE_SELECT_FEATURE_ENTITY,
      bestFitToggle: features.bestFitToggle ?? DEFAULT_BEST_FIT_FEATURE_ENTITY,
      calculationMethod:
        features.calculationMethod ?? DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY,
      apiResponse: chart.response,
      request: chart.request,
      options: chart.options ?? "{}",
      locks: chart.locks ?? UNDEFINED_LOCKS,
      mpcLocks: chart.mpcLocks ?? UNDEFINED_MPC_LOCKS,
      lassoBrushEnd: (features.lasso?.properties?.brushEnd ?? false) as boolean,
      lassoBrushPosition: features.lasso?.properties?.position as {
        x: number;
        y: number;
      },
      lassoBrushList: (features.lasso?.properties?.list as string[]) ?? [],
      features: chart.features ?? UNDEFINED_CHART_FEATURES,
      pressureChartSettings:
        features.pressureChartSettings ?? DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY,
      totalFluidChartSettings:
        features.totalFluidChartSettings ?? DEFAULT_TOTAL_FLUID_CHART_SETTINGS_ENTITY,
      logAxis: features.logAxis ?? DEFAULT_LOG_AXIS_FEATURE_ENTITY,
      useProbitLineOfBestFitData:
        features.useProbitLineOfBestFitData ?? DEFAULT_FEATURE_ENTITY,
      useLineOfBestFit: features.useLineOfBestFit ?? DEFAULT_FEATURE_ENTITY,
      showZeros: features.showZeros ?? DEFAULT_FEATURE_ENTITY,
      hideOthers: features.hideOthers ?? DEFAULT_FEATURE_ENTITY,
      quarterSlope: features.quarterSlope ?? DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
      halfSlope: features.halfSlope ?? DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
      unitSlope: features.unitSlope ?? DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
      highlight: chart.highlight ?? true,
      contextMenu: chart.contextMenu ?? null,
      licensedVolumeLine: features.licensedVolumeLine ?? DEFAULT_FEATURE_ENTITY,
      xAxisFieldSync: features.xAxisFieldSync ?? DEFAULT_FEATURE_ENTITY,
      xAxisSort: features.xAxisSort ?? DEFAULT_X_AXIS_SORT_ENTITY,
      multiProduct: features.multiProduct ?? DEFAULT_MULTI_PRODUCT_ENTITY,
      recoveryPVIRadius: features.recoveryPVIRadius ?? DEFAULT_FEATURE_ENTITY,
      splitYear: features.splitYear ?? DEFAULT_FEATURE_ENTITY
    };
  }, [chart, chartFocus]);
};

/**
 * ChartEntities interface - defines the shape of all chart entity state values.
 * This is defined as an explicit interface rather than ReturnType<typeof useChartEntities>
 * to allow other interfaces to extend it (e.g., SetChartPayload in automation).
 */
export interface ChartEntities {
  chartType: EvaChart;
  overrideSizeLimit: boolean;
  product: Maybe<string>;
  sum: typeof DEFAULT_FEATURE_ENTITY;
  forecast: typeof DEFAULT_FEATURE_ENTITY;
  source: typeof DEFAULT_SOURCE_FEATURE_ENTITY;
  timeStep: typeof DEFAULT_TIME_STEP_FEATURE_ENTITY;
  lasso: typeof DEFAULT_LASSO_FEATURE_ENTITY;
  isProducingRateType: typeof DEFAULT_SOURCE_FEATURE_ENTITY;
  cutoff: Maybe<(typeof DEFAULT_CUT_OFF_FEATURE_ENTITY)["properties"]["value"]>;
  shutInMonths: Maybe<
    (typeof DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY)["properties"]["value"]
  >;
  movingAverageDays: Maybe<
    (typeof DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY)["properties"]["value"]
  >;
  survivorBias: Maybe<boolean>;
  survivorBiasAsForecast: typeof DEFAULT_FEATURE_ENTITY;
  useWeightedRatioAverage: Maybe<boolean>;
  lockUnits: Maybe<boolean>;
  debugState: typeof DEFAULT_FEATURE_ENTITY;
  typewells: typeof DEFAULT_FEATURE_ENTITY;
  workingForecast: typeof DEFAULT_FEATURE_ENTITY;
  fullScreen: typeof DEFAULT_FEATURE_ENTITY;
  screenshot: typeof DEFAULT_SCREENSHOT_FEATURE_ENTITY;
  average: typeof DEFAULT_AVERAGE_FEATURE_ENTITY;
  p10: typeof DEFAULT_P10_FEATURE_ENTITY;
  p50: typeof DEFAULT_P50_FEATURE_ENTITY;
  p90: typeof DEFAULT_P90_FEATURE_ENTITY;
  normalizeBy: typeof DEFAULT_NORMALIZATION_ENTITY;
  chartFocus: ReturnType<typeof useChartFocusUnitCorrection>;
  multiProductFocus: typeof DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY;
  showWellCount: Maybe<boolean>;
  chartLegend: typeof DEFAULT_FEATURE_ENTITY;
  bounds: Maybe<DOMRect>;
  dataTable: typeof DEFAULT_DATA_TABLE_FEATURE_ENTITY;
  overflowIndex: Maybe<number>;
  pointOpacity: typeof DEFAULT_POINT_OPACITY_FEATURE_ENTITY;
  pointSize: typeof DEFAULT_POINT_SIZE_FEATURE_ENTITY;
  lineSize: typeof DEFAULT_LINE_SIZE_FEATURE_ENTITY;
  lineOpacity: typeof DEFAULT_LINE_OPACITY_FEATURE_ENTITY;
  highlightedLineSize: typeof DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY;
  highlightedLineOpacity: typeof DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY;
  useSeriesStyleOverride: typeof DEFAULT_FEATURE_ENTITY;
  chartTypeParam: typeof DEFAULT_CHART_TYPE_PARAM_FEATURE;
  annotations: typeof DEFAULT_FEATURE_ENTITY;
  histogramToggle: typeof DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY;
  jitterPlotToggle: typeof DEFAULT_JITTER_PLOT_FEATURE_ENTITY;
  violinPlotToggle: typeof DEFAULT_VIOLIN_PLOT_FEATURE_ENTITY;
  boxPlotBins: Partial<BoxPlotBinsFeature>;
  dataRangeSelect: typeof DEFAULT_DATA_RANGE_SELECT_FEATURE_ENTITY;
  bestFitToggle: typeof DEFAULT_BEST_FIT_FEATURE_ENTITY;
  calculationMethod: typeof DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY;
  apiResponse: unknown; // transient — populated during render, cleared after
  request: Maybe<ChartRequestType>;
  options: string;
  locks: Partial<
    Record<"xMin" | "xMax" | "yMin" | "yMax" | "y2Min" | "y2Max", LockState>
  >;
  mpcLocks: MpcAxisLockMap;
  lassoBrushEnd: boolean;
  lassoBrushPosition: { x: number; y: number };
  lassoBrushList: string[];
  features: typeof UNDEFINED_CHART_FEATURES;
  pressureChartSettings: typeof DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY;
  totalFluidChartSettings: typeof DEFAULT_TOTAL_FLUID_CHART_SETTINGS_ENTITY;
  logAxis: typeof DEFAULT_LOG_AXIS_FEATURE_ENTITY;
  useProbitLineOfBestFitData: typeof DEFAULT_FEATURE_ENTITY;
  useLineOfBestFit: typeof DEFAULT_FEATURE_ENTITY;
  showZeros: typeof DEFAULT_FEATURE_ENTITY;
  hideOthers: typeof DEFAULT_FEATURE_ENTITY;
  quarterSlope: typeof DEFAULT_SLOPE_LINE_FEATURE_ENTITY;
  halfSlope: typeof DEFAULT_SLOPE_LINE_FEATURE_ENTITY;
  unitSlope: typeof DEFAULT_SLOPE_LINE_FEATURE_ENTITY;
  highlight: boolean;
  contextMenu: { point: Maybe<{ x: number; y: number }>; active: boolean };
  licensedVolumeLine: typeof DEFAULT_FEATURE_ENTITY;
  xAxisFieldSync: typeof DEFAULT_FEATURE_ENTITY;
  xAxisSort: typeof DEFAULT_X_AXIS_SORT_ENTITY;
  multiProduct: typeof DEFAULT_MULTI_PRODUCT_ENTITY;
  recoveryPVIRadius: typeof DEFAULT_FEATURE_ENTITY;
  splitYear: typeof DEFAULT_FEATURE_ENTITY;
}

// Default entities defined inline to avoid circular reference with ChartEntities type
export const DEFAULT_CHART_ENTITIES = {
  chartType: EvaChart.None,
  overrideSizeLimit: false,
  product: undefined,
  sum: DEFAULT_FEATURE_ENTITY,
  forecast: DEFAULT_FEATURE_ENTITY,
  source: DEFAULT_SOURCE_FEATURE_ENTITY,
  timeStep: DEFAULT_TIME_STEP_FEATURE_ENTITY,
  lasso: DEFAULT_LASSO_FEATURE_ENTITY,
  isProducingRateType: DEFAULT_SOURCE_FEATURE_ENTITY,
  cutoff: DEFAULT_CUT_OFF_FEATURE_ENTITY?.properties?.value,
  shutInMonths: DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY?.properties?.value,
  movingAverageDays: DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY?.properties?.value,
  survivorBias: DEFAULT_FEATURE_ENTITY?.active,
  survivorBiasAsForecast: DEFAULT_FEATURE_ENTITY,
  useWeightedRatioAverage: DEFAULT_FEATURE_ENTITY?.active,
  lockUnits: DEFAULT_FEATURE_ENTITY?.active,
  debugState: DEFAULT_FEATURE_ENTITY,
  typewells: DEFAULT_FEATURE_ENTITY,
  workingForecast: DEFAULT_FEATURE_ENTITY,
  fullScreen: DEFAULT_FEATURE_ENTITY,
  screenshot: DEFAULT_SCREENSHOT_FEATURE_ENTITY,
  average: DEFAULT_AVERAGE_FEATURE_ENTITY,
  p10: DEFAULT_P10_FEATURE_ENTITY,
  p50: DEFAULT_P50_FEATURE_ENTITY,
  p90: DEFAULT_P90_FEATURE_ENTITY,
  normalizeBy: DEFAULT_NORMALIZATION_ENTITY,
  chartFocus: DEFAULT_CHART_FOCUS_ENTITY,
  multiProductFocus: DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY,
  multiProduct: DEFAULT_MULTI_PRODUCT_ENTITY,
  showWellCount: DEFAULT_FEATURE_ENTITY?.active,
  chartLegend: DEFAULT_FEATURE_ENTITY,
  bounds: undefined,
  dataTable: DEFAULT_DATA_TABLE_FEATURE_ENTITY,
  overflowIndex: undefined,
  pointOpacity: DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  pointSize: DEFAULT_POINT_SIZE_FEATURE_ENTITY,
  lineSize: DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  lineOpacity: DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  highlightedLineSize: DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  highlightedLineOpacity: DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  useSeriesStyleOverride: DEFAULT_FEATURE_ENTITY,
  chartTypeParam: DEFAULT_CHART_TYPE_PARAM_FEATURE,
  annotations: DEFAULT_FEATURE_ENTITY,
  histogramToggle: DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY,
  jitterPlotToggle: DEFAULT_JITTER_PLOT_FEATURE_ENTITY,
  violinPlotToggle: DEFAULT_VIOLIN_PLOT_FEATURE_ENTITY,
  boxPlotBins: DEFAULT_BOX_PLOT_BINS_ENTITY,
  dataRangeSelect: DEFAULT_DATA_RANGE_SELECT_FEATURE_ENTITY,
  bestFitToggle: DEFAULT_BEST_FIT_FEATURE_ENTITY,
  calculationMethod: DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY,
  apiResponse: undefined,
  request: undefined,
  options: "{}",
  locks: UNDEFINED_LOCKS,
  mpcLocks: UNDEFINED_MPC_LOCKS,
  lassoBrushEnd: false as boolean,
  lassoBrushPosition: ORIGIN as { x: number; y: number },
  lassoBrushList: [] as string[],
  features: UNDEFINED_CHART_FEATURES,
  pressureChartSettings: DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY,
  totalFluidChartSettings: DEFAULT_TOTAL_FLUID_CHART_SETTINGS_ENTITY,
  logAxis: DEFAULT_LOG_AXIS_FEATURE_ENTITY,
  useProbitLineOfBestFitData: DEFAULT_FEATURE_ENTITY,
  useLineOfBestFit: DEFAULT_FEATURE_ENTITY,
  showZeros: DEFAULT_FEATURE_ENTITY,
  hideOthers: DEFAULT_FEATURE_ENTITY,
  quarterSlope: DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
  halfSlope: DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
  unitSlope: DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
  highlight: true,
  contextMenu: null,
  licensedVolumeLine: DEFAULT_FEATURE_ENTITY,
  xAxisFieldSync: DEFAULT_FEATURE_ENTITY,
  xAxisSort: DEFAULT_X_AXIS_SORT_ENTITY,
  recoveryPVIRadius: DEFAULT_FEATURE_ENTITY,
  splitYear: DEFAULT_FEATURE_ENTITY
} as const;
